import {
  MouseEventHandler,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { ResultTable, StyledPage } from "@src/components";
import { LdButton, LdNotice, LdTypo } from "@liquid/react";
import { SequenceInputForm } from "./Tabs";
import { SubmitHandler } from "react-hook-form";
import {
  TranscriptResponse,
  SequenceInputTypes,
  OutputQueryResponse,
  ParalogResponseType,
  SortProps,
} from "@src/types";
import {
  StyledAnchor,
  StyledModal,
  StyledPanelContainer,
} from "./HomePage.style";
import { StyledColumn, StyledRow } from "@src/styles";
import {
  useGetTranscriptQuery,
  useGetResultDataQuery,
  useGetOnlyDNAResultDataQuery,
} from "@src/query/editor/EditorQuery";
import { errorHandler, transformRequest } from "@src/utils";
import { ResultView } from "./Tabs/ResultView";
import { useLocation, useNavigate } from "react-router-dom";
import { paths } from "@src/router/paths";
import { PotentialOfftarget } from "./Tabs/PotentialOfftarget";
import { SEQ_VALIDATE_REGX, TARGET_TABLE_LABEL } from "@src/constants";
import { showNotification } from "@src/hooks/useNotification";
import { ShowProgress } from "@src/components/ShowProgress/ShowProgress";
import { NotFoundPage } from "../NotFoundPage";
import { RootState, useAppDispatch } from "@src/features/store/store";
import { updateTranscript } from "@src/features/Slices/DataSlice";
import { useSelector } from "react-redux";
import { getSortStates, sortTableData } from "@src/utils/tableUtils";
import { ResultTableCustom } from "@src/components";
import { ROWS_PER_PAGE, CRIPSER_PATH } from "@src/config";
import useIframeHeight from "@src/components/IframeHeight/Iframe";

const loaderPropsTranscript = {
  headerText: "In progress",
  message: "Fetching transcript details, please wait",
  cancelable: false,
};

const loaderPropsResult = {
  headerText: "In progress",
  message: "Analysis is in progress, please wait",
  cancelable: false,
};

const initiValue = {
  data: {
    off_target_analysis_file: "",
    results_table: [],
  },
  igv: { locus: "", project_id: "" },
  error_message: [""],
};

// Default sort option for transcript as need to do CUSTOM sort by default, so sort key is not required
const defaultSortOption: SortProps = {
  sort: "",
  sortOrder: "desc",
  pIndex: null,
};

function HomePage({ output, offtarget }: any) {
  const [loading, setLoading] = useState(false); // Transcript loading flag
  const [loadingResult, setLoadingResult] = useState(false); // loading output result flag
  const [selectedGenome, setSelectedGenome] = useState(""); // Genome selected from dropdown
  const [disableSubmit, setDisableSubmit] = useState(true); // disable submit button flag
  const [selectedEditWindow, setSelectedEditWindow] = useState<string[]>([]);
  const [isDNAOnly, setIsDNAOnly] = useState(false);
  const [inputSequence, setInputSequence] = useState<string>("");
  const [isGenomeSpecific, setIsGenomeSpecific] = useState<boolean>(true);

  const location = useLocation();

  // transcript result holding state if single gene hit
  const [result, setResult] = useState<TranscriptResponse | undefined>(
    undefined
  );
  // transcript result holding state for all cases
  const [transcriptResult, setTranscriptResult] = useState<
    TranscriptResponse | undefined
  >(undefined);
  const [selectedTranscript, setSelectedTranscript] = useState<any>([]);
  // hold prev selected transcript to roll back if cancels from modal
  const [prevSelectedTranscript, setPrevSelectedTranscript] = useState<any>([]);
  const [selectedMode, setSelectedMode] = useState<string>("base");
  const [formData, setFormData] = useState<any>([]);
  // selected Gene Symbol if paralog result
  const [selectedGeneSymbol, setSelectedGeneSymbol] = useState("");
  const [isParalog, setParalog] = useState(false);
  // transcript result holding state for paralog
  const [paralogResult, setParalogResult] = useState<
    ParalogResponseType[] | undefined
  >(undefined);
  // transcrpt processed flag to enable/disable CLEAR/SHOW TRANSCRIPT buttons
  const [transcriptLoaded, setTranscriptLoaded] = useState(false);
  const [showTranscriptModal, setTranscriptModalShow] =
    useState<boolean>(false);
  const [outputResult, setOutputResult] = useState<
    OutputQueryResponse | undefined
  >(initiValue);

  const navigate = useNavigate();

  const tableDef = useMemo(
    () => ({
      onRowSelect: (data: []) => setSelectedTranscript(data),
      tableTitle: "Select Transcript",
      uniqueKey: "transcript_accession",
      single: true,
      multiple: false,
      columns: [
        { name: "Gene Symbol", sortable: false, dataKey: "gene_symbol" },
        { name: "Accession", sortable: true, dataKey: "transcript_accession" },
        {
          name: "Description",
          sortable: true,
          dataKey: "transcript_description",
        },
        {
          name: "Transcript Length",
          sortable: true,
          dataKey: "transcript_length",
        },
        {
          name: "Transcript Status",
          sortable: true,
          dataKey: "transcript_status",
        },
      ],
    }),
    []
  );

  // PAGINATION STATES
  const [isLoading, setTableLoading] = useState(false);
  const [currentPage, setcurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(Number(ROWS_PER_PAGE[0]));
  const [currentSort, setCurrenSort] = useState<SortProps>(defaultSortOption);
  const [perPageData, setPerPageData] = useState<any>(undefined);
  const [sortedData, setSortedData] = useState<any>(undefined);

  const dispatch = useAppDispatch();

  const handlePageSizeChange = (pageSize: number) => {
    setcurrentPage(0);
    setRowsPerPage(pageSize);
  };

  const handlePageChange = (value: number) => {
    setcurrentPage(value);
  };

  const handleSort = useCallback(
    (ev: any) => {
      const { sortOrder, sort, pIndex } = getSortStates({
        ev,
        tableDef,
        singleSelect: tableDef.single,
      });
      setCurrenSort({ sortOrder, sort, pIndex });
    },
    [tableDef]
  );

  const transcriptData = useSelector(
    (state: RootState) => state.data.transcriptData
  );

  /** slice data after sorting to current rows per page and current page */

  useEffect(() => {
    if (transcriptData && transcriptData.data) {
      /** Update result data with checked rows if available */
      let transData = JSON.parse(JSON.stringify(transcriptData));
      if (sortedData && sortedData.data?.transcript_details.length) {
        transData.data?.transcript_details.forEach(
          (item: any, index: number) => {
            const match = sortedData.data?.transcript_details.find(
              (item2: any) =>
                item2.transcript_accession === item.transcript_accession
            );
            item.selected = match?.selected;
          }
        );
      }
      // updation ends

      const { pageData, sortedElements } = sortTableData({
        data: transData?.data?.transcript_details,
        currentSort,
        currentPage,
        rowsPerPage,
      });
      const pgData = {
        ...transcriptData,
        data: { transcript_details: pageData },
      };
      const srtData = {
        ...transcriptData,
        data: { transcript_details: sortedElements },
      };
      setTableLoading(true);
      setPerPageData([]);
      setTimeout(() => {
        setPerPageData(pgData);
        setSortedData(srtData);
        setTableLoading(false);
      }, 100);
    }
  }, [currentPage, currentSort, transcriptData, rowsPerPage]);

  // onSelect and onSelectAll for table interations

  const onSelect = useCallback(
    (ev: any) => {
      const { uniqueKey, multiple, single } = tableDef;

      // take out DATA part of sorted data
      const srtData = sortedData?.data?.transcript_details;
      const pgData = perPageData?.data?.transcript_details;
      const selected =
        typeof ev.detail.selected === "boolean"
          ? ev.detail.selected
          : ev.detail;
      const elementNumber = single
        ? ev.target.value
        : ev.currentTarget
          ? pgData[ev.detail.rowIndex][uniqueKey]
          : srtData[ev.detail.rowIndex][uniqueKey];
      const updatedElements = srtData.map((e: any) => {
        return {
          ...e,
          selected:
            e[uniqueKey] === elementNumber
              ? selected
              : multiple
                ? e.selected
                : undefined,
        };
      });
      const pgDataNew = updatedElements?.slice(
        currentPage * rowsPerPage,
        currentPage * rowsPerPage + rowsPerPage
      );
      if (tableDef.onRowSelect && typeof tableDef.onRowSelect === "function") {
        tableDef.onRowSelect(
          updatedElements.filter((el: any) => el.selected && el)
        );
      }
      setPerPageData({
        ...perPageData,
        data: { transcript_details: pgDataNew },
      });
      setSortedData({
        ...sortedData,
        data: { transcript_details: updatedElements },
      });
    },
    [tableDef, sortedData, perPageData, currentPage, rowsPerPage]
  );

  // Paralog transcript modal
  const modalRef = useRef<any>(null);

  const { mutateAsync: getTransMutate } = useGetTranscriptQuery(setLoading);
  const { mutateAsync: getResultMutate } =
    useGetResultDataQuery(setLoadingResult);

  const { mutateAsync: getDNAResultMutate } =
    useGetOnlyDNAResultDataQuery(setLoadingResult);

  //Adding logic for height
  useIframeHeight(CRIPSER_PATH);

  // Clear transcript table on mount
  useEffect(() => {
    onClear();
    setLoading(false);
    setLoadingResult(false);
  }, [location.key]);

  const getUniqueGenes: any = (data: {}[]) => {
    const setResponse = data.map((elm: any): [string, string] => [
      elm.gene_symbol,
      elm.gene_description,
    ]);
    const mapSet = new Map(setResponse);
    return Array.from(mapSet).map(([gene_symbol, gene_description]) => ({
      gene_symbol,
      gene_description,
    }));
  };

  const processTranscriptResponse = (data: TranscriptResponse) => {
    setTranscriptResult(data);
    setLoading(false);
    const len =
      (data.data?.transcript_details && data.data?.transcript_details.length) ??
      0;
    // check unique Genes
    const uniqueGenes = getUniqueGenes(data.data?.transcript_details);
    setParalog(uniqueGenes.length > 1);
    if (uniqueGenes.length > 1) {
      // paralog gene
      setParalogResult(uniqueGenes);
    } else {
      // single gene
      setResult(data);
      dispatch(updateTranscript(data));
      if (len) {
        setDisableSubmit(false);
      } else if (data.data?.transcript_message.length) {
        setDisableSubmit(false);
      } else {
        setDisableSubmit(true);
      }
    }
  };

  useEffect(() => {
    if (formData?.genome_specific_editing === "no")
      onSubmitResult();
  }, [formData])

  const onSubmit: SubmitHandler<SequenceInputTypes> = async (payload) => {
    // reset selected transcript data

    setSelectedTranscript([]);

    const mode = sessionStorage.getItem("mode");
    mode && setSelectedMode(mode);
    if (mode && mode === "codon" && !payload?.gene_id) {
      showNotification({
        type: "alert",
        content: "Please select Gene Symbol/Gene ID to proceed",
      });
      return false;
    }
    setLoading(true);
    const request = transformRequest(
      payload,
      "transcript",
      mode ? mode : "base"
    );
    // persist form data for later use with submit for result tab
    setFormData({ ...payload, project_id: request.project_id });
    setInputSequence(payload.user_input_sequence ?? "");
    setSelectedGenome(payload.genome);
    setSelectedEditWindow(payload.edit_window.split(","));
    if (payload?.genome_specific_editing === "no") {
      setIsDNAOnly(true);
      return;
    }
    setIsDNAOnly(false);
    setLoading(true);

    try {
      const { data }: any = await getTransMutate(request);
      processTranscriptResponse(data);
      // update transcript processed flag
      setTranscriptLoaded(true);
    } catch (err) {
      errorHandler(err);
    }
  };

  const handleSubmitResult: MouseEventHandler<HTMLLdButtonElement> = () => {
    onSubmitResult()
  }

  const onSubmitResult = async () => {
    let payload;
    if (formData?.genome_specific_editing !== "no") {
      if (
        result?.data?.transcript_details?.length &&
        selectedTranscript?.length <= 0
      ) {
        showNotification({
          type: "alert",
          content: "Please select Transcript from the table to proceed",
        });
        return false;
      }
      const selected_transcript_id =
        selectedTranscript && selectedTranscript.length
          ? selectedTranscript[0].transcript_accession
          : "null";
      const selected_gene_id =
        selectedTranscript && selectedTranscript.length
          ? selectedTranscript[0]?.gene_id
          : "null";
      const fetch_transcript_list_random_id =
        selectedTranscript && selectedTranscript.length
          ? selectedTranscript[0].fetch_transcript_list_random_id
          : result?.data.transcript_message[0]?.split("#")[1];
      payload = {
        ...formData,
        selected_transcript_id,
        selected_gene_id,
        fetch_transcript_list_random_id,
      };
    }
    if (formData?.genome_specific_editing === "no") {
      payload = {
        user_id: formData?.user_id,
        project_id: formData?.project_id,
        grna_length: formData?.grna_length,
        pam_type: formData?.pam_type,
        edit_window: formData?.edit_window,
        cbe_type: formData?.cbe_type,
        base_editor_type: formData?.base_editor_type,
        analysis_module: "dna_base_editing_only",
        //user_input_sequence: formData?.user_input_sequence,
        user_input_sequence: SEQ_VALIDATE_REGX.test(formData.user_input_sequence)
          ? formData.user_input_sequence
            .replaceAll(/\n+/g, "\n")
            .replaceAll(/\n[atgc]+/g, (match: any) => match.toUpperCase())
          : `>sequence\n${formData.user_input_sequence
            .replaceAll(/\n+/g, "\n")
            .replaceAll(/[atgc]+/g, (match: any) => match.toUpperCase())}`,
      }
    }

    setLoadingResult(true);
    const mode = sessionStorage.getItem("mode");
    mode && setSelectedMode(mode);
    const request = transformRequest(payload, "result", mode ? mode : "base");

    try {
      const apiCall = formData?.genome_specific_editing === "no" ? getDNAResultMutate : getResultMutate
      const { data }: any = await apiCall(formData?.genome_specific_editing === "no" ? payload : request);

      // reset transcript loaded variable
      setTranscriptLoaded(false);
      let newData: OutputQueryResponse;
      if (data && data.data && data.data.results_table) {
        newData = {
          ...data,
          data: {
            ...data.data,
            results_table: data.data.results_table.map((item: any) => {
              const arrItems = item.off_target_analysis
                .split("#")
                .map((value: string, index: number) => ({
                  [TARGET_TABLE_LABEL[index]]: value,
                }));
              return {
                ...item,
                ...arrItems[0],
                ...arrItems[1],
                ...arrItems[2],
                ...arrItems[3],
              };
            }),
            cbe_type: formData.cbe_type,
          },
          igv: {
            projectId: request.project_id,
            locus: data.igv?.locus,
          },
        };
        setOutputResult(newData);
      } else {
        setOutputResult({ ...initiValue, error_message: data.error_message });
      }
      setTimeout(() => {
        navigate(paths.output, { state: newData });
      }, 100);
    } catch (err) {
      errorHandler(err);
    }
  };

  useEffect(() => {
    // popup Transcript table on gene symbol selection changes
    if (selectedGeneSymbol) {
      const curResultSet = transcriptResult?.data?.transcript_details;
      const filterByGeneResultSet = curResultSet?.filter(
        (trans: any) => trans.gene_symbol === selectedGeneSymbol
      );
      // update transcript table data with selected from paralog table
      dispatch(
        updateTranscript({
          ...transcriptResult,
          data: { transcript_details: filterByGeneResultSet },
        })
      );
      setTranscriptModalShow(true);
      // store currently selected transcript to roll back if required
      setPrevSelectedTranscript(selectedTranscript);
    }
  }, [selectedGeneSymbol, result, transcriptResult]);

  const onGeneSymbolSelect = (gene_symbol: string) => {
    setSelectedGeneSymbol(gene_symbol);
  };
  const tableDefParalog = {
    onRowSelect: (data: any) => setSelectedGeneSymbol(data),
    tableTitle: "Select Gene (Click on any gene symbol to view Accession)",
    uniqueKey: "transcript_accession",
    columns: [
      {
        name: "Gene Symbol",
        sortable: false,
        dataKey: "gene_symbol",
        key: "gene_symbol",
        render: (text: string, row: any) => (
          <StyledAnchor mode="ghost" onClick={() => onGeneSymbolSelect(text)}>
            {text}
          </StyledAnchor>
        ),
      },
      {
        name: "Description",
        sortable: false,
        dataKey: "gene_description",
        key: "gene_description",
      },
    ],
  };

  const tableDefNoRadio = {
    onRowSelect: (data: []) => setSelectedTranscript(data),
    uniqueKey: "transcript_accession",
    removeToolbar: true,
    columns: [
      { name: "Accession", dataKey: "transcript_accession" },
      {
        name: "Description",
        dataKey: "transcript_description",
      },
      {
        name: "Transcript Length",
        dataKey: "transcript_length",
      },
      {
        name: "Transcript Status",
        dataKey: "transcript_status",
      },
    ],
  };

  const onClear = () => {
    setResult(undefined);
    setParalogResult(undefined);
    setParalog(false);
    setTranscriptLoaded(false);
    setSelectedGeneSymbol("");
    setTranscriptResult(undefined);

    // reset transcript data
    dispatch(updateTranscript([])); // clear transcript table state
    setSortedData([]);
    setPerPageData([]);
    setCurrenSort(defaultSortOption);

    setTranscriptModalShow(false);
  };

  const onModalClose = (selected: boolean) => {
    if (!selected) {
      // Cancel button seelcted, rollback selected transcript
      setSelectedTranscript(prevSelectedTranscript);
      setSelectedGeneSymbol("");
    }
    setTranscriptModalShow(false);
  };

  const renderOutputView = () => {
    if (outputResult && outputResult.data) {
      return (
        <ResultView
          data={outputResult.data}
          selectedGenome={selectedGenome}
          message={outputResult?.error_message}
          mode={selectedMode}
          igv={outputResult.igv}
          selectedEditWindow={selectedEditWindow}
          inputSequence={inputSequence}
          isGenomeSpecific={isGenomeSpecific}
        />
      );
    }
    return <NotFoundPage />;
  };

  const renderOfftargetView = () => {
    return (
      <StyledPanelContainer>
        <PotentialOfftarget />
      </StyledPanelContainer>
    );
  };

  const renderModalView = () => {
    return (
      <StyledModal
        ref={modalRef}
        open={showTranscriptModal}
        style={{ width: "98%" }}
        cancelable={false}
      >
        <LdTypo slot="header">{selectedGeneSymbol}</LdTypo>
        <StyledRow>
          <StyledColumn width="100">
            <ResultTableCustom
              data={sortedData.data?.transcript_details}
              tableDef={tableDef}
              // pagination props
              onPageChange={handlePageChange}
              onSort={handleSort}
              loading={isLoading}
              totalPage={sortedData?.data?.transcript_details.length}
              defaultSort={{ columnIndex: 2, sortOrder: "desc" }}
              currentPage={currentPage}
              rowsPerPage={rowsPerPage}
              dataInPage={perPageData?.data?.transcript_details}
              onPageSizeChange={handlePageSizeChange}
              pageSize={rowsPerPage}
              onSelect={onSelect}
            />
          </StyledColumn>
        </StyledRow>
        <LdButton
          slot="footer"
          style={{ width: "8rem" }}
          mode="ghost"
          onClick={() => {
            onModalClose(false);
            modalRef?.current.close();
          }}
        >
          Cancel
        </LdButton>
        <LdButton
          slot="footer"
          style={{ width: "8rem" }}
          onClick={() => {
            onModalClose(true);
            modalRef?.current.close();
          }}
        >
          OK
        </LdButton>
      </StyledModal>
    );
  };
  return (
    <StyledPage variant={output ? "result" : "home"}>
      {output ? (
        renderOutputView()
      ) : offtarget ? (
        renderOfftargetView()
      ) : (
        <StyledPanelContainer>
          <StyledRow direction="column">
            <StyledColumn width={100}>
              <SequenceInputForm
                onSubmit={onSubmit}
                onClear={onClear}
                transcriptLen={transcriptLoaded}
                loading={loading || loadingResult}
                onGenomeSpecificChange={setIsGenomeSpecific}
              />
              {/* Transcript loader — only for genome-specific path */}
              {loading && !isDNAOnly && (
                <ShowProgress {...loaderPropsTranscript} loading={loading} />
              )}

              {/* Result loader — for DNA-only path */}
              {loadingResult && isDNAOnly && (
                <ShowProgress {...loaderPropsResult} loading={loadingResult} />
              )}


              {!loading && isParalog && paralogResult?.length && (
                <>
                  <ResultTable
                    data={paralogResult}
                    tableDef={tableDefParalog}
                    labelText="Genes associated with target sequence"
                    pagination={false}
                  />
                  {selectedGeneSymbol && selectedTranscript.length ? (
                    <ResultTable
                      data={selectedTranscript.map((it: any) => ({
                        ...it,
                        selected: undefined,
                      }))}
                      tableDef={tableDefNoRadio}
                      labelText={`Selected Gene : ${selectedGeneSymbol}`}
                      removeToolbar={true}
                      pagination={false}
                    />
                  ) : undefined}
                  <LdButton
                    style={{
                      width: "18rem",
                      margin: "10px auto",
                    }}
                    disabled={
                      paralogResult.length <= 0 ||
                      loadingResult ||
                      selectedTranscript.length <= 0
                    }
                    onClick={handleSubmitResult}
                  >
                    Submit
                  </LdButton>
                  {loadingResult && (
                    <ShowProgress
                      {...loaderPropsResult}
                      loading={loadingResult}
                    />
                  )}
                </>
              )}
              {!isParalog &&
                !loading &&
                sortedData &&
                !!sortedData?.data?.transcript_details.length ? (
                <>
                  <ResultTableCustom
                    data={sortedData.data?.transcript_details}
                    tableDef={tableDef}
                    // pagination props
                    onPageChange={handlePageChange}
                    onSort={handleSort}
                    loading={isLoading}
                    totalPage={sortedData?.data?.transcript_details.length}
                    defaultSort={{ columnIndex: 2, sortOrder: "desc" }}
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    dataInPage={perPageData?.data?.transcript_details}
                    onPageSizeChange={handlePageSizeChange}
                    pageSize={rowsPerPage}
                    onSelect={onSelect}
                  />
                  <LdButton
                    style={{
                      width: "18rem",
                      margin: "10px auto",
                    }}
                    disabled={
                      disableSubmit ||
                      loadingResult ||
                      selectedTranscript.length <= 0
                    }
                    onClick={onSubmitResult}
                  >
                    Submit
                  </LdButton>
                  {loadingResult && (
                    <ShowProgress
                      {...loaderPropsResult}
                      loading={loadingResult}
                    />
                  )}
                </>
              ) : !loading &&
                result &&
                !!result?.data?.transcript_message.length ? (
                <>
                  <ShowTranscriptMesage
                    msg={result?.data?.transcript_message}
                  />
                  <LdButton
                    style={{
                      width: "18rem",
                      margin: "10px auto",
                    }}
                    disabled={disableSubmit || loadingResult}
                    onClick={onSubmitResult}
                  >
                    Submit
                  </LdButton>
                  {loadingResult && (
                    <ShowProgress
                      {...loaderPropsResult}
                      loading={loadingResult}
                    />
                  )}
                </>
              ) : !loading && result && !!result?.error_message.length ? (
                <ShowTranscriptMesage
                  msg={result?.error_message}
                  headLine="Error"
                  mode="error"
                />
              ) : null}
            </StyledColumn>
          </StyledRow>
          {showTranscriptModal && renderModalView()}
        </StyledPanelContainer>
      )}
    </StyledPage>
  );
}

export { HomePage };

export const ShowTranscriptMesage = ({
  msg,
  mode = "info",
  headLine = "Information",
}: any) => {
  return (
    <LdNotice headline={headLine} mode={mode}>
      {msg.map((m: string, i: number) => (
        <p key={`mag-key-${i}`}>{m.split("#")[0]}</p>
      ))}
    </LdNotice>
  );
};
